import { c as _c } from "react/compiler-runtime";
import { AddStepButton } from "../add-step-button";
import { useCarePlanContext } from "../care-plan";
import { DateConnector } from "../date-connector";
import { SortableStepCard } from "../sortable-step-card";
import { StepCard } from "../step-card";
import { StepCardPopover } from "../step-card-popover";
import { type CarePlanStep, CarePlanStepStatus, StepType, branchContainer, collectDescendantBranchIds, findStep, findStepContainer, isLinkedMessage, isPatientStep } from "../types";
import { CollisionPriority } from "@dnd-kit/abstract";
import { useDroppable } from "@dnd-kit/react";
import { useMemo } from "react";
import { flattenStepsWithBranchChildren } from "@/lib/care-plan-cycle-utils";
import { cx } from "@/lib/tailwind-utils";
import { useBranch } from "./branch";
import { DecisionStep } from "./decision-step";
export function BranchStepList() {
  const $ = _c(30);
  const {
    branch
  } = useBranch();
  const {
    config: t0,
    cycles: t1,
    getNextStep
  } = useCarePlanContext();
  const {
    readOnly,
    detailMode,
    orientation
  } = t0;
  const {
    onlyShowCurrentPasses,
    cycles,
    cycleFamilyMap
  } = t1;
  let t2;
  bb0: {
    if (!onlyShowCurrentPasses || !cycles || cycles.length === 0) {
      t2 = branch.steps;
      break bb0;
    }
    let visibleIterations;
    if ($[0] !== branch.steps || $[1] !== cycleFamilyMap) {
      const allBranchSteps = flattenStepsWithBranchChildren(branch.steps);
      const passStatus = new Map();
      for (const step of allBranchSteps) {
        if (!isPatientStep(step) || !step.cycleId || !step.cyclePass) {
          continue;
        }
        const key = `${step.cycleId}:${step.cyclePass}`;
        const done = step.status === CarePlanStepStatus.COMPLETED || step.status === CarePlanStepStatus.SKIPPED;
        const prev = passStatus.get(key);
        passStatus.set(key, prev === undefined ? done : prev && done);
      }
      visibleIterations = new Set();
      const processedFamilies = new Set();
      const branchCycleIds = Array.from(new Set(allBranchSteps.filter(_temp).map(_temp2)));
      for (const cid of branchCycleIds) {
        const familyIds = cycleFamilyMap.get(cid);
        const familyKey = familyIds ? Array.from(familyIds).sort().join(",") : cid;
        if (processedFamilies.has(familyKey)) {
          continue;
        }
        processedFamilies.add(familyKey);
        const memberIds = familyIds ?? new Set([cid]);
        const iterations = [];
        const iterationMinStep = new Map();
        for (const step_0 of allBranchSteps) {
          if (!isPatientStep(step_0) || !step_0.cycleId || !step_0.cyclePass) {
            continue;
          }
          if (!memberIds.has(step_0.cycleId)) {
            continue;
          }
          const key_0 = `${step_0.cycleId}:${step_0.cyclePass}`;
          const prev_0 = iterationMinStep.get(key_0);
          if (prev_0 === undefined || step_0.stepNumber < prev_0) {
            iterationMinStep.set(key_0, step_0.stepNumber);
          }
        }
        for (const [key_1, minStep] of Array.from(iterationMinStep.entries())) {
          const [id, passStr] = key_1.split(":");
          iterations.push({
            cycleId: id,
            pass: Number(passStr),
            minStep
          });
        }
        iterations.sort(_temp3);
        let found = false;
        for (const iter of iterations) {
          const key_2 = `${iter.cycleId}:${iter.pass}`;
          if (!(passStatus.get(key_2) ?? false)) {
            visibleIterations.add(key_2);
            found = true;
            break;
          }
        }
        if (!found && iterations.length > 0) {
          visibleIterations.add(`${iterations[0].cycleId}:${iterations[0].pass}`);
        }
      }
      $[0] = branch.steps;
      $[1] = cycleFamilyMap;
      $[2] = visibleIterations;
    } else {
      visibleIterations = $[2];
    }
    let t3;
    if ($[3] !== branch.steps || $[4] !== visibleIterations) {
      let t4;
      if ($[6] !== visibleIterations) {
        t4 = step_1 => {
          if (!isPatientStep(step_1) || !step_1.cycleId || !step_1.cyclePass) {
            return true;
          }
          return visibleIterations.has(`${step_1.cycleId}:${step_1.cyclePass}`);
        };
        $[6] = visibleIterations;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = branch.steps.filter(t4);
      $[3] = branch.steps;
      $[4] = visibleIterations;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const visibleBranchSteps = t2;
  let t3;
  if ($[8] !== visibleBranchSteps) {
    t3 = visibleBranchSteps.filter(_temp4);
    $[8] = visibleBranchSteps;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const timelineSteps = t3;
  const usePopover = detailMode === "popover";
  const isHorizontal = orientation === "horizontal";
  if (readOnly) {
    const t4 = isHorizontal ? "flex-row items-center" : "flex-col items-center";
    let t5;
    if ($[10] !== t4) {
      t5 = cx("flex gap-2", t4);
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== isHorizontal || $[13] !== timelineSteps || $[14] !== usePopover) {
      t6 = timelineSteps.map((step_2, i) => <div key={step_2.id} className={cx("flex", isHorizontal ? "flex-row items-center" : "flex-col items-center")}>{i === 0 && <DateConnector minutesGap={step_2.minutesAfterPrevious} />}{usePopover ? <StepCardPopover stepId={step_2.id}><StepCard step={step_2} /></StepCardPopover> : <StepCard step={step_2} />}{step_2.type === StepType.DECISION && <DecisionStep.Root step={step_2}><DecisionStep.BranchDisplay>{[...(step_2.branches ?? [])].sort(_temp5).map(_temp6)}</DecisionStep.BranchDisplay></DecisionStep.Root>}{i < timelineSteps.length - 1 && <DateConnector minutesGap={timelineSteps[i + 1].minutesAfterPrevious} />}</div>);
      $[12] = isHorizontal;
      $[13] = timelineSteps;
      $[14] = usePopover;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== t5 || $[17] !== t6) {
      t7 = <div className={t5}>{t6}</div>;
      $[16] = t5;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  const t4 = branch.id;
  let t5;
  if ($[19] !== timelineSteps[0] || $[20] !== timelineSteps.length) {
    t5 = timelineSteps.length > 0 && <DateConnector minutesGap={timelineSteps[0].minutesAfterPrevious} />;
    $[19] = timelineSteps[0];
    $[20] = timelineSteps.length;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== getNextStep || $[23] !== timelineSteps) {
    t6 = timelineSteps.map((step_3, i_0) => {
      const nextStep = getNextStep(timelineSteps, i_0);
      const connector = nextStep ? <DateConnector minutesGap={nextStep.minutesAfterPrevious} /> : null;
      if (step_3.type === StepType.DECISION) {
        return <DecisionStep.Root key={step_3.id} step={step_3}><SortableStepCard step={step_3} index={i_0} /><DecisionStep.BranchDisplay>{[...(step_3.branches ?? [])].sort(_temp7).map(_temp8)}</DecisionStep.BranchDisplay>{connector}</DecisionStep.Root>;
      }
      return <SortableStepCard key={step_3.id} step={step_3} index={i_0}>{connector}</SortableStepCard>;
    });
    $[22] = getNextStep;
    $[23] = timelineSteps;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AddStepButton />;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== branch.id || $[27] !== t5 || $[28] !== t6) {
    t8 = <BranchDropZone branchId={t4}>{t5}{t6}{t7}</BranchDropZone>;
    $[26] = branch.id;
    $[27] = t5;
    $[28] = t6;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}

/**
 * Acts as a droppable target for the whole branch so cross-container drags
 * land even when the branch has no sortable items to collide with. The
 * sortable-step-card type ("care-plan-step") is the only thing accepted.
 */
function _temp8(b_3) {
  return <DecisionStep.Branch key={b_3.id} branch={b_3}><DecisionStep.BranchLabel /><DecisionStep.BranchStepList /></DecisionStep.Branch>;
}
function _temp7(a_1, b_2) {
  return a_1.position - b_2.position;
}
function _temp6(b_1) {
  return <DecisionStep.Branch key={b_1.id} branch={b_1}><DecisionStep.BranchLabel /><DecisionStep.BranchStepList /></DecisionStep.Branch>;
}
function _temp5(a_0, b_0) {
  return a_0.position - b_0.position;
}
function _temp4(s_1) {
  return !isLinkedMessage(s_1);
}
function _temp3(a, b) {
  return a.minStep - b.minStep;
}
function _temp2(s_0) {
  return (s_0 as CarePlanStep & {
    cycleId: string;
  }).cycleId;
}
function _temp(s) {
  return isPatientStep(s) && s.cycleId;
}
function BranchDropZone(t0) {
  const $ = _c(12);
  const {
    branchId,
    children
  } = t0;
  const {
    state: t1,
    lookups: t2
  } = useCarePlanContext();
  const {
    steps
  } = t1;
  const {
    patientId
  } = t2;
  const restrictToSameContainer = patientId !== undefined;
  let t3;
  if ($[0] !== branchId) {
    t3 = branchContainer(branchId);
    $[0] = branchId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== branchId || $[3] !== restrictToSameContainer || $[4] !== steps) {
    t4 = source => {
      if (restrictToSameContainer && findStepContainer(steps, String(source.id)) !== branchContainer(branchId)) {
        return false;
      }
      const sourceStep = findStep(steps, String(source.id));
      if (!sourceStep || sourceStep.type !== StepType.DECISION) {
        return true;
      }
      return !collectDescendantBranchIds(sourceStep).has(branchId);
    };
    $[2] = branchId;
    $[3] = restrictToSameContainer;
    $[4] = steps;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      id: t3,
      type: "care-plan-branch",
      accept: t4,
      collisionPriority: CollisionPriority.Low
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    ref
  } = useDroppable(t5);
  let t6;
  if ($[9] !== children || $[10] !== ref) {
    t6 = <div ref={ref} className="flex flex-col items-center gap-2">{children}</div>;
    $[9] = children;
    $[10] = ref;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}